"use client";

import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCreateInventoryItem, type QuantityType, FULLNESS_OPTIONS, FULLNESS_THRESHOLD_OPTIONS } from "@/hooks/useInventory";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { InventoryItemNameCombobox } from "@/components/owner/InventoryItemNameCombobox";
import { HelpCircle, Image, Save, Trash2, Upload } from "lucide-react";

export interface AddInventoryItemFormRoom {
  id: string;
  name: string;
}

export interface AddInventoryItemFormProps {
  propertyId: string;
  rooms: AddInventoryItemFormRoom[];
  /** When true, room selection is required (e.g. cleaner flow). */
  roomRequired?: boolean;
  /** Pre-selected room id when opening from cleaner (e.g. Kitchen). */
  defaultRoomId?: string | null;
  onSuccess: (item: { id: string; item_name: string; room_id: string | null; [key: string]: unknown }) => void;
  onCancel: () => void;
}

export function AddInventoryItemForm({
  propertyId,
  rooms,
  roomRequired = false,
  defaultRoomId = null,
  onSuccess,
  onCancel,
}: AddInventoryItemFormProps) {
  const createItem = useCreateInventoryItem();
  const [itemName, setItemName] = useState("");
  const [currentCount, setCurrentCount] = useState(0);
  const [thresholdCount, setThresholdCount] = useState(0);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [roomId, setRoomId] = useState<string | null>(defaultRoomId);
  const [quantityType, setQuantityType] = useState<QuantityType>("units");
  const [currentValue, setCurrentValue] = useState<number>(1.0);
  const [thresholdValue, setThresholdValue] = useState<number>(0.25);
  const [isAppliance, setIsAppliance] = useState(false);
  const [brand, setBrand] = useState("");
  const [modelNumber, setModelNumber] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [trackThreshold, setTrackThreshold] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoUpload = async (file: File): Promise<string | null> => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image file", variant: "destructive" });
      return null;
    }
    const ext = file.name.split(".").pop() || "jpg";
    const filePath = `${propertyId}/inventory-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("reference-photos").upload(filePath, file, { upsert: false });
    if (error) {
      console.error("Upload error:", error);
      throw error;
    }
    const { data: urlData } = supabase.storage.from("reference-photos").getPublicUrl(filePath);
    return urlData.publicUrl;
  };

  const handleFileSelect = async (file: File) => {
    setUploadingPhoto(true);
    try {
      const url = await handlePhotoUpload(file);
      if (url) {
        setPhotoUrl(url);
        toast({ title: "Photo uploaded", description: "Image added to item" });
      }
    } catch {
      toast({ title: "Error", description: "Failed to upload photo", variant: "destructive" });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    if (!itemName.trim()) {
      toast({ title: "Error", description: "Item name is required", variant: "destructive" });
      return;
    }
    if (roomRequired && rooms.length > 0 && !roomId) {
      toast({ title: "Error", description: "Please select a room", variant: "destructive" });
      return;
    }
    try {
      const data = await createItem.mutateAsync({
        propertyId,
        itemName: itemName.trim(),
        currentCount,
        thresholdCount: trackThreshold ? thresholdCount : null,
        photoUrl,
        roomId: roomId || null,
        quantityType,
        currentValue: quantityType === "fullness" ? currentValue : null,
        thresholdValue: trackThreshold && quantityType === "fullness" ? thresholdValue : null,
        brand: brand.trim() || null,
        modelNumber: modelNumber.trim() || null,
        serialNumber: serialNumber.trim() || null,
        inventoryScope: "supply_closet",
      });
      toast({ title: "Success", description: "Item added" });
      onSuccess(data as { id: string; item_name: string; room_id: string | null; [key: string]: unknown });
    } catch {
      toast({ title: "Error", description: "Failed to save item", variant: "destructive" });
    }
  };

  const showRoomSection = rooms.length > 0;
  const roomLabel = roomRequired ? "Room *" : "Room (optional)";

  return (
    <div className="space-y-6">
      {/* Photo */}
      <div className="space-y-2">
        <Label>Item Photo (optional)</Label>
        <div className="flex items-start gap-4">
          <div className="w-20 h-20 rounded-md border border-border bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
            {photoUrl ? (
              <img src={photoUrl} alt="Item preview" className="w-full h-full object-cover" />
            ) : (
              <Image className="w-6 h-6 text-muted-foreground" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto}>
              <Upload className="w-4 h-4 mr-2" />
              {photoUrl ? "Change Photo" : "Add Photo"}
            </Button>
            {photoUrl && (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setPhotoUrl(null)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Photo
              </Button>
            )}
          </div>
        </div>
        <input
          type="file"
          accept="image/*"
          className="hidden"
          ref={fileInputRef}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              handleFileSelect(file);
              e.target.value = "";
            }
          }}
        />
      </div>

      {/* Room */}
      {showRoomSection && (
        <div className="space-y-2">
          <Label>{roomLabel}</Label>
          <Select value={roomId || "none"} onValueChange={(v) => setRoomId(v === "none" ? null : v)}>
            <SelectTrigger className="bg-background">
              <SelectValue placeholder="Select a room" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="none">{roomRequired ? "— Select room —" : "No room"}</SelectItem>
              {rooms.map((room) => (
                <SelectItem key={room.id} value={room.id}>
                  {room.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Item Name */}
      <div className="space-y-2">
        <Label>Item Name</Label>
        <InventoryItemNameCombobox value={itemName} onChange={setItemName} placeholder="Search or type item name…" />
      </div>

      {/* Appliance fields */}
      <div className="space-y-3">
        <div className="flex items-center justify-between rounded-md border p-4">
          <Label htmlFor="appliance-toggle" className="cursor-pointer flex-1">
            This is an appliance / durable item
          </Label>
          <Switch
            id="appliance-toggle"
            checked={isAppliance}
            onCheckedChange={(checked) => {
              if (!checked) {
                setBrand("");
                setModelNumber("");
                setSerialNumber("");
              }
              setIsAppliance(checked);
            }}
          />
        </div>
        {isAppliance && (
          <div className="grid grid-cols-1 gap-3 pl-1">
            <div className="space-y-2">
              <Label>Brand (optional)</Label>
              <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. KitchenAid" />
            </div>
            <div className="space-y-2">
              <Label>Model number (optional)</Label>
              <Input value={modelNumber} onChange={(e) => setModelNumber(e.target.value)} placeholder="e.g. KSM55" />
            </div>
            <div className="space-y-2">
              <Label>Serial number (optional)</Label>
              <Input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} placeholder="e.g. 12345678" />
            </div>
          </div>
        )}
      </div>

      {/* Quantity Type */}
      <div className="space-y-2">
        <Label>Quantity Type</Label>
        <Select value={quantityType} onValueChange={(v) => setQuantityType(v as QuantityType)}>
          <SelectTrigger className="bg-background">
            <SelectValue placeholder="Select quantity type" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            <SelectItem value="units">Units (count)</SelectItem>
            <SelectItem value="fullness">Fullness (bottle level)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Track threshold */}
      <div className="flex items-center justify-between rounded-md border p-4">
        <div>
          <Label htmlFor="track-threshold-toggle" className="cursor-pointer">
            Track threshold
          </Label>
          <p className="text-sm text-muted-foreground mt-0.5">Notify when this item is low or needs restocking</p>
        </div>
        <Switch id="track-threshold-toggle" checked={trackThreshold} onCheckedChange={setTrackThreshold} />
      </div>

      {/* Units: current count + threshold */}
      {quantityType === "units" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Current Count</Label>
            <Input
              type="number"
              min={0}
              placeholder="0"
              value={currentCount === 0 ? "" : currentCount}
              onChange={(e) => setCurrentCount(e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0))}
            />
          </div>
          {trackThreshold && (
            <div className="space-y-2 my-[11px]">
              <div className="flex items-center gap-1">
                <Label>Threshold</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[220px]">
                      <p className="text-xs">When count drops below this number, you'll be alerted to restock.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                type="number"
                min={0}
                placeholder="0"
                value={thresholdCount === 0 ? "" : thresholdCount}
                onChange={(e) => setThresholdCount(e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0))}
              />
              <p className="text-sm text-muted-foreground mt-1">We'll notify you when this item's count hits or goes below this threshold.</p>
            </div>
          )}
        </div>
      )}

      {/* Fullness */}
      {quantityType === "fullness" && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Current Fullness Level</Label>
            <div className="flex flex-wrap gap-2">
              {FULLNESS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setCurrentValue(option.value)}
                  className={cn(
                    "px-3 py-2 rounded-full text-sm font-medium transition-colors border min-w-[80px] touch-manipulation",
                    currentValue === option.value ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground border-border hover:bg-muted/80"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          {trackThreshold && (
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label>Threshold (Fullness Level)</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[220px]">
                      <p className="text-xs">We'll notify you when this item reaches or goes below this fullness level.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Select value={thresholdValue.toString()} onValueChange={(v) => setThresholdValue(parseFloat(v))}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Select threshold level" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  {FULLNESS_THRESHOLD_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-1">We'll notify you when this item reaches or goes below this fullness level.</p>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button onClick={handleSave} disabled={createItem.isPending}>
          <Save className="w-4 h-4 mr-2" />
          Add Item
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
